/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {environment} from '../../../environments/environment';
import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {Observable} from 'rxjs';
import {PartNumberChangeBaseModel} from './contracts/part-number-change-base.model';
import {PartNumberChangeDetailContract} from './contracts/part-number-change-detail.contract';
import {Cacheable} from '../../decorators/cacheable.decorator';
import {PartNumberChangeQueryModel} from './contracts/part-number-change-query.model';
import {PartNumberChangeResultModel} from './contracts/part-number-change-result.model';
import {PartNumberChangeStatusBaseModel} from './contracts/status/model/part-number-change-status-base.model';
import {PartNumberChangeTypeBaseModel} from './contracts/pnc-type/model/part-number-change-type-base.model';
import {PartNumberChangeTrialCommandModel} from './contracts/part-number-change-trial-command.model';
import {PartNumberChangeRejectCommandModel} from './contracts/part-number-change-reject-command.model';
import {PartNumberChangeSolveCommandModel} from './contracts/part-number-change-solve-command.model';
import {PartNumberChangeConfigModel} from './contracts/part-number-change-config.model';
import {PartNumberChangeCreateSolveContract} from './contracts/part-number-change-create-solve.contract';
import {PartNumberChangeCommandModel} from './contracts/part-number-change-command.model';
import {PartNumberChangeCancelCommandContract} from './contracts/part-number-change-cancel-command.contract';
import {PartNumberChangeAcceptCommandContract} from './contracts/part-number-change-accept-command.contract';

@Injectable({
    providedIn: 'root'
})
export class PartNumberChangesApi {

    private partNumberChangesBaseUrl = `${environment.partNumberChangesBaseUrl}/api/partNumberChange`;
    private partNumberDetailUrl = `${this.partNumberChangesBaseUrl}/detail`;
    private partNumberSearchUrl = `${this.partNumberChangesBaseUrl}/search`;
    private partNumberTodoUrl = `${this.partNumberChangesBaseUrl}/toDo`;
    private partNumberMyListUrl = `${this.partNumberChangesBaseUrl}/myList`;

    private httpOptions = {
        headers: new HttpHeaders({
            'accept': 'application/json',
            'Content-Type': 'application/json'
        })
    };

    constructor(private http: HttpClient) {
    }

    @Cacheable()
    getPartNumberChangeConfig(): Observable<PartNumberChangeConfigModel> {
        return this.http.get<PartNumberChangeConfigModel>(`${this.partNumberChangesBaseUrl}/config`);
    }

    /**
     * This method creates a new part number change
     * @param pncCommand the part number change command
     */
    createPartNumberChange(pncCommand: PartNumberChangeCommandModel): Observable<PartNumberChangeBaseModel> {
        return this.http.post<PartNumberChangeBaseModel>(this.partNumberChangesBaseUrl, pncCommand, this.httpOptions);
    }

    /**
     * This method deletes a part number change
     * @param pncNumber the pnc number
     */
    deletePartNumberChanges(pncNumber: string): Observable<void> {
        return this.http.delete<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}`, this.httpOptions);
    }

    /**
     * This method rejects a part number change
     * @param pncNumber the pnc number
     * @param pncCommandReject the part number change command reject
     */
    rejectPartNumberChange(pncNumber: string, pncCommandReject: PartNumberChangeRejectCommandModel): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/reject`, pncCommandReject, this.httpOptions);
    }


    /**
     * This method rejects a part number change by EE Specialist
     * @param pncNumber the pnc number
     * @param pncCommandReject the part number change command reject
     */
    rejectPartNumberChangeSolution(pncNumber: string, pncCommandReject: PartNumberChangeRejectCommandModel): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/rejectSolution`, pncCommandReject, this.httpOptions);
    }

    /**
     * This method accepts a part number change by EE Specialist
     * @param pncNumber the pnc number
     * @param pncCommandAccept the part number change command accept
     */
    acceptPartNumberChangeSolution(pncNumber: string, pncCommandAccept: PartNumberChangeAcceptCommandContract): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/acceptSolution`, pncCommandAccept, this.httpOptions);
    }

    /**
     * This method cancels a part number change
     * @param pncNumber the pnc number
     * @param pncCommandCancel the part number change command cancel
     */
    cancelPartNumberChange(pncNumber: string, pncCommandCancel: PartNumberChangeCancelCommandContract): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/cancel`, pncCommandCancel, this.httpOptions);
    }


    /**
     * This method sends a part number change
     * @param pncNumber the pnc number
     * @param pncTrialCommand the part number change trial command
     */
    sendPartNumberChange(pncNumber: string, pncTrialCommand: PartNumberChangeTrialCommandModel): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/send`, pncTrialCommand, this.httpOptions);
    }

    /**
     * This method solves a part number change
     * @param pncNumber the pnc number
     * @param pncCommandSolve the part number change solve command
     */
    solvePartNumberChange(pncNumber: string, pncCommandSolve: PartNumberChangeSolveCommandModel): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/solve`, pncCommandSolve, this.httpOptions);
    }

    /**
     * This method send a solution to a EESpecialist validate
     * @param pncNumber the pnc number
     * @param pncCommandSolve the part number change solve command
     */
    sendPartNumberChangeSolutionToValidate(pncNumber: string, pncCommandSolve: PartNumberChangeSolveCommandModel): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/sendSolution`,
                pncCommandSolve, this.httpOptions);
    }


    /**
     * This method solves a part number change
     * @param pncCommandCreateSolve the part number change create and solve command
     */
    createAndSolvePartNumberChange(pncCommandCreateSolve: PartNumberChangeCreateSolveContract): Observable<PartNumberChangeBaseModel> {
        return this.http.post<PartNumberChangeBaseModel>(`${this.partNumberChangesBaseUrl}/solve`, pncCommandCreateSolve, this.httpOptions);
    }

    /**
     * This method retrieves the part number changes
     * @param pncNumber the pnc number
     */
    getPartNumberChange(pncNumber: string): Observable<PartNumberChangeDetailContract> {
        return this.http.get<PartNumberChangeDetailContract>(`${this.partNumberDetailUrl}/${pncNumber}`, this.httpOptions);
    }

    /**
     * Gets PartNumberChanges from the BE
     * @param partNumberChangesQuery the search query criteria
     */
    getPartNumberChanges = (partNumberChangesQuery: PartNumberChangeQueryModel): Observable<PartNumberChangeResultModel> =>
            this.http.post<PartNumberChangeResultModel>(`${this.partNumberSearchUrl}`, partNumberChangesQuery, this.httpOptions);

    /**
     * Gets PartNumberChangesTodo from the BE
     * @param partNumberChangesQuery the search query criteria
     */
    getPartNumberChangesToDo = (partNumberChangesQuery: PartNumberChangeQueryModel): Observable<PartNumberChangeResultModel> =>
            this.http.post<PartNumberChangeResultModel>(`${this.partNumberTodoUrl}`, partNumberChangesQuery, this.httpOptions);

    /**
     * Returns PartNumberChangesTodo count from the BE
     */
    getPartNumberChangesToDoCount(): Observable<number> {
        return this.http.get<number>(`${this.partNumberTodoUrl}/count`, this.httpOptions);
    }

    /**
     * Gets PartNumberChangesMyList from the BE
     * @param partNumberChangesQuery the search query criteria
     */
    getPartNumberChangesMyList = (partNumberChangesQuery: PartNumberChangeQueryModel): Observable<PartNumberChangeResultModel> =>
            this.http.post<PartNumberChangeResultModel>(`${this.partNumberMyListUrl}`, partNumberChangesQuery, this.httpOptions);

    /**
     * This method retrieves the part number changes status
     */
    @Cacheable()
    getAllPartNumberChangeStatusBase(): Observable<PartNumberChangeStatusBaseModel[]> {
        return this.http.get<PartNumberChangeStatusBaseModel[]>(`${this.partNumberChangesBaseUrl}/status/base`, this.httpOptions);
    }

    /**
     * This method retrieves the part number changes types
     */
    @Cacheable()
    getAllPartNumberChangeTypeBase(): Observable<PartNumberChangeTypeBaseModel[]> {
        return this.http.get<PartNumberChangeTypeBaseModel[]>(`${this.partNumberChangesBaseUrl}/type/base`, this.httpOptions);
    }

    /**
     * This method retrieves the available part number changes types
     */
    @Cacheable()
    getAvailablePartNumberChangeTypeBase(): Observable<PartNumberChangeTypeBaseModel[]> {
        return this.http.get<PartNumberChangeTypeBaseModel[]>(`${this.partNumberChangesBaseUrl}/type/base/available`, this.httpOptions);
    }

    /**
     * This method marks a part number change as done
     * @param pncNumber the pnc number
     */
    markPartNumberChangeAsDone(pncNumber: string): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/done`, this.httpOptions);
    }

    /**
     * This method marks a part number change as undone
     * @param pncNumber the pnc number
     */
    markPartNumberChangeAsUndone(pncNumber: string): Observable<void> {
        return this.http.put<void>(`${this.partNumberChangesBaseUrl}/${pncNumber}/undone`, this.httpOptions);
    }
}
